import { type AssetManifest, type CellRect, type CompiledChunk, type ManifestObjectAppearance } from "daedalus-core";
import { MapPictureCache, PICTURE_CHUNKS_PER_STEP, type DomSurface, type MapPicture, type PageImage, type PictureAtlas } from "daedalus-renderer-phaser";
import { minimapCropCovers, type GroundPicture } from "odyssey-shared";
import { chunkPictureLayers } from "./feed.ts";
import { ShrineArt, type LandmarkTally } from "./landmarks.ts";

/**
 * The game's pictures of the booted map (P1.9-T178; ADR-061's amendment of 2026-10-14, ADR-100 decisions 3 and 4): the
 * editor overview's painter and cache, fed by `chunkPictureLayers` from the client chunks the page already holds. A
 * consumer asks for a picture by a slot name and a scale: the minimap's field and the Nearby window's are the slots
 * `hud` and `nearby`, and the world map window (P2.12-T6) asks for its own at its own scale. A slot holds one picture,
 * freed when the slot is released or asks for another scale, once the picture made in its place has been laid over with it,
 * scaled (ADR-100's note of 2026-10-15), so a zoom step never shows a bare field. A picture is composed a few chunks a step, the ones
 * nearest the player first, so the part around the player is there within a frame or two and the rest fills in behind.
 */

/**
 * What the pictures draw things from (ADR-100 decisions 54 and 58): the world version's appearance table, the shrine art
 * the character has discovered, and a count of the landmarks a chunk could not place.
 */
export interface PictureThings {
  readonly appearances: readonly ManifestObjectAppearance[];
  readonly art: ShrineArt | null;
  readonly tally: LandmarkTally;
}

/**
 * The map the pictures are of, as the booted world gives it.
 */
export interface PictureMap {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly chunkSize: number;
  readonly tileSize: number;
}

/**
 * A slot's picture as a consumer reads it: the canvas to copy from, its size and crop (`GroundPicture`), and a
 * version that moves whenever its pixels may have changed. The same object is handed back for the slot's life.
 */
export interface PictureView extends GroundPicture {
  readonly image: CanvasImageSource;
  readonly version: number;
}

/**
 * What a consumer asks of the pictures.
 */
export interface MapPictureSource {
  /**
   * The picture of a slot at a scale, made when the slot has none or has another scale, and drawn about a position
   * when the map is longer than one picture holds.
   *
   * @param slot The consumer's slot.
   * @param scale Pixels a tile.
   * @param x The player's position across, in tiles.
   * @param y The player's position down, in tiles.
   * @param fieldWidth The consumer's field width in pixels, which the picture must cover.
   * @param fieldHeight The consumer's field height in pixels.
   * @returns The picture, or null when the browser made no canvas.
   */
  view(slot: string, scale: number, x: number, y: number, fieldWidth: number, fieldHeight: number): PictureView | null;
  /**
   * Frees a slot's picture.
   *
   * @param slot The consumer's slot.
   */
  release(slot: string): void;
  /**
   * Composes the chunks owed, for one slot only, the player's nearest first.
   *
   * @param x The player's position across, in tiles.
   * @param y The player's position down, in tiles.
   * @returns True when a chunk was composed, so a consumer draws again.
   */
  step(x: number, y: number): boolean;
  /**
   * How many chunks a slot's picture still owes; absent where a source cannot tell.
   *
   * @param slot The consumer's slot.
   * @returns The count.
   */
  owed?(slot: string): number;
  /**
   * True for pictures a crossing's change of stage gave (ADR-147 decision 19), which a field holds its last drawing for
   * while they are bare; absent or false for any other.
   */
  readonly crossing?: boolean;
  /**
   * The ground of the placed neighbours drawn beside the map (ADR-147 decision 6), which a field draws at their offset so
   * the minimap carries on past a seam as the world view does; absent where the page draws none.
   */
  readonly beside?: BesideSource;
}

/**
 * One neighbour's picture as a field draws it: the picture, or null while it has none, and the player's position in the
 * neighbour's own tiles. A consumer keeps these records and has them filled in place.
 */
export interface BesideField {
  ground: PictureView | null;
  x: number;
  y: number;
}

/**
 * The neighbours' pictures a field asks for beside the map's own.
 */
export interface BesideSource {
  /**
   * Fills a slot's neighbour pictures about the player, one record each, growing the list only the first time.
   *
   * @param slot The consumer's slot.
   * @param scale Pixels a tile.
   * @param x The player's position across, in the owning map's tiles.
   * @param y The player's position down.
   * @param fieldWidth The field's width in pixels.
   * @param fieldHeight The field's height in pixels.
   * @param out The records to fill.
   * @returns How many records hold a neighbour.
   */
  views(slot: string, scale: number, x: number, y: number, fieldWidth: number, fieldHeight: number, out: BesideField[]): number;
  /**
   * Frees a slot's neighbour pictures.
   *
   * @param slot The consumer's slot.
   */
  release(slot: string): void;
}

/**
 * The milliseconds a step may spend composing chunks after the one it always composes when any is owed. A chunk of
 * Aethervale at 2 px a tile takes about 3 ms in the page measured on (P1.9-T178's report), so a frame composes one
 * chunk and a second only when the first was cheap: a 256 by 256 map fills in about a second, and a frame never takes
 * more than a chunk or two beyond its own work.
 */
export const PICTURE_BUDGET_MS = 1;

/**
 * The most chunks a step composes whatever the clock says.
 */
export const PICTURE_MOST_A_STEP = PICTURE_CHUNKS_PER_STEP;

/**
 * Frames between a retry of the chunks drawn without an atlas page they lacked.
 */
export const PICTURE_RETRY_FRAMES = 120;

/**
 * The retry rounds a slot gets after a chunk of it threw: past these the chunks set aside are not queued again, so a
 * bad chunk is reported a few times and not for ever.
 */
export const PICTURE_MAX_RETRIES = 3;

/**
 * One slot: its cache key, scale, picture and the view consumers read.
 */
interface Slot {
  readonly key: string;
  readonly scale: number;
  readonly picture: MapPicture<PageImage>;
  readonly canvas: DomSurface;
  changes: number;
  done: boolean;
  threw: boolean;
  badRounds: number;
  readonly view: { image: CanvasImageSource; width: number; height: number; cropX: number; cropY: number; version: number };
}

/**
 * The pictures of the booted map.
 */
export class GameMapPictures implements MapPictureSource {
  private readonly cache: MapPictureCache<PageImage>;
  private readonly slots = new Map<string, Slot>();
  private readonly list: Slot[] = [];
  private readonly near = { cx: 0, cy: 0 };
  private generation = 0;
  private frames = 0;
  private composedTotal = 0;
  private mostInAStep = 0;

  /**
   * @param map The map pictured.
   * @param chunks Its client chunks by `cx,cy`.
   * @param assets The asset manifest the frames are named in.
   * @param surfaces Makes a canvas of a size in pixels, or null when the browser cannot.
   * @param atlas The loaded atlas pages the pictures copy from.
   * @param budgetMs The milliseconds a step may spend composing after its first chunk.
   * @param now The clock the budget is read from, in milliseconds.
   * @param active Whether the page is ready to spend frame time on pictures; nothing is composed while it is not, so a
   *   world still loading is not slowed.
   * @param fail Told of a chunk that could not be composed; the browser's `reportError` when omitted.
   * @param things The appearances, shrine art and tally the landmarks are drawn from; no things when omitted.
   */
  public constructor(
    private readonly map: PictureMap,
    private readonly chunks: ReadonlyMap<string, CompiledChunk>,
    private readonly assets: AssetManifest,
    surfaces: (width: number, height: number) => DomSurface | null,
    atlas: PictureAtlas<PageImage>,
    private readonly budgetMs: number = PICTURE_BUDGET_MS,
    private readonly now: () => number = () => performance.now(),
    private readonly active: () => boolean = () => true,
    private readonly fail: (error: unknown) => void = (error) => reportError(error),
    private readonly things: PictureThings = { appearances: [], art: null, tally: { skipped: 0 } },
  ) {
    this.cache = new MapPictureCache<PageImage>(surfaces, atlas, 1);
  }

  /**
   * The pictures held.
   *
   * @returns The count.
   */
  public get size(): number {
    return this.slots.size;
  }

  /**
   * The chunks composed in all since the pictures were made.
   *
   * @returns The count.
   */
  public get composed(): number {
    return this.composedTotal;
  }

  /**
   * The most chunks any one step composed since the pictures were made.
   *
   * @returns The count.
   */
  public get most(): number {
    return this.mostInAStep;
  }

  /**
   * The memory of the pictures' canvases, 4 bytes a pixel.
   *
   * @returns Bytes.
   */
  public get bytes(): number {
    let total = 0;
    for (const slot of this.slots.values()) {
      total += slot.canvas.width * slot.canvas.height * 4;
    }
    return total;
  }

  /**
   * Chunks a slot's picture still owes.
   *
   * @param slot The slot.
   * @returns The count, 0 for a slot with no picture.
   */
  public owed(slot: string): number {
    const held = this.slots.get(slot);
    return held === undefined ? 0 : this.cache.pending(held.key, held.scale);
  }

  /**
   * The landmarks the pictures could not place, for an appearance past the table or a frame the manifest lacks.
   *
   * @returns The count.
   */
  public get skippedLandmarks(): number {
    return this.things.tally.skipped;
  }

  /**
   * Draws again the chunks that cells touch, in every picture held, for shrine art that arrived or left. A picture that
   * is not made yet reads the art when it is.
   *
   * @param rects The cells, `x0` and `y0` inclusive, `x1` and `y1` exclusive.
   */
  public redraw(rects: readonly CellRect[]): void {
    for (const slot of this.list) {
      let touched = 0;
      for (const rect of rects) {
        touched += this.cache.markDirty(slot.key, rect);
      }
      if (touched > 0) {
        slot.done = false;
      }
    }
  }

  /**
   * Makes a slot's picture, crop centred on the player when the map is longer than a picture holds.
   *
   * @param name The slot.
   * @param scale Pixels a tile.
   * @param x The player's position across, in tiles.
   * @param y The player's position down, in tiles.
   * @param under The slot whose picture the new one is laid over with, scaled, or null for none.
   * @returns The slot, or null when no canvas can be made.
   */
  private open(name: string, scale: number, x: number, y: number, under: Slot | null): Slot | null {
    this.generation += 1;
    const key = `${this.map.id}#${name}#${this.generation}`;
    const { width, height, chunkSize, tileSize } = this.map;
    const feed = { tileSize, assets: this.assets, appearances: this.things.appearances, art: this.things.art, tally: this.things.tally };
    const picture = this.cache.request({ map: key, scale, revision: 1, width, height, chunkSize, tileSize, manifest: this.assets, feed: (cx, cy) => chunkPictureLayers(this.chunks, feed, cx, cy), centre: { x: Math.floor(x), y: Math.floor(y) } }, { cx: Math.floor(x / chunkSize), cy: Math.floor(y / chunkSize) });
    if (picture === null) {
      return null;
    }
    const canvas = picture.surface as DomSurface;
    const slot: Slot = { key, scale, picture, canvas, changes: 1, done: false, threw: false, badRounds: 0, view: { image: canvas.canvas, width: canvas.width, height: canvas.height, cropX: picture.crop.x, cropY: picture.crop.y, version: 1 } };
    this.slots.set(name, slot);
    this.list.push(slot);
    if (under !== null) {
      this.carry(under, slot);
    }
    return slot;
  }

  /**
   * The slot, other than one by a name, whose picture has the most detail and has had a chunk composed.
   *
   * @param name The name left out, the slot a new picture is being made for.
   * @returns The slot, or null when no other slot has anything drawn.
   */
  private richest(name: string): Slot | null {
    let best: Slot | null = null;
    for (const [held, slot] of this.slots) {
      if (held !== name && slot.changes > 1 && (best === null || slot.scale > best.scale)) {
        best = slot;
      }
    }
    return best;
  }

  /**
   * Lays one slot's picture over another's, scaled to the other's scale and placed by the two crops, in one copy.
   * A picture made at a new scale starts as the old one drawn larger or smaller, never bare, and each chunk
   * composed over it replaces its part (the painter clears a chunk's rectangle first), so a zoom step shows the
   * ground it had at once and the sharper ground a chunk at a time.
   *
   * @param from The slot copied.
   * @param onto The new slot.
   */
  private carry(from: Slot, onto: Slot): void {
    const context = onto.canvas.context;
    const ratio = onto.scale / from.scale;
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.imageSmoothingEnabled = false;
    context.drawImage(from.canvas.canvas, 0, 0, from.canvas.width, from.canvas.height, (from.picture.crop.x - onto.picture.crop.x) * onto.scale, (from.picture.crop.y - onto.picture.crop.y) * onto.scale, from.canvas.width * ratio, from.canvas.height * ratio);
    context.restore();
  }

  /**
   * Frees a slot's picture and forgets the slot, unless a newer picture already holds its name.
   *
   * @param name The slot.
   * @param slot Its record.
   */
  private close(name: string, slot: Slot): void {
    this.cache.drop(slot.key);
    if (this.slots.get(name) === slot) {
      this.slots.delete(name);
    }
    this.list.splice(this.list.indexOf(slot), 1);
  }

  /**
   * The picture of a slot at a scale. A picture of another scale, or a cropped one that no longer holds the field,
   * is replaced by a new one laid over with it, scaled, and then freed; a slot with none to replace, or one with nothing drawn yet, is laid over
   * with the drawn picture of most detail among the others.
   *
   * @param name The slot.
   * @param scale Pixels a tile.
   * @param x The player's position across, in tiles.
   * @param y The player's position down, in tiles.
   * @param fieldWidth The consumer's field width in pixels.
   * @param fieldHeight The consumer's field height in pixels.
   * @returns The picture, or null when no canvas can be made.
   */
  public view(name: string, scale: number, x: number, y: number, fieldWidth: number, fieldHeight: number): PictureView | null {
    let slot = this.slots.get(name);
    let replaced: Slot | undefined;
    if (slot !== undefined && slot.scale !== scale) {
      replaced = slot;
      slot = undefined;
    }
    if (slot !== undefined && !minimapCropCovers(x, y, slot.picture.crop, slot.canvas.width / scale, slot.canvas.height / scale, this.map, fieldWidth / scale, fieldHeight / scale)) {
      replaced = slot;
      slot = undefined;
    }
    if (slot === undefined) {
      const made = this.open(name, scale, x, y, replaced !== undefined && replaced.changes > 1 ? replaced : (this.richest(name) ?? replaced ?? null));
      if (replaced !== undefined) {
        this.close(name, replaced);
      }
      slot = made ?? undefined;
    }
    if (slot === undefined) {
      return null;
    }
    slot.view.version = slot.changes;
    return slot.view;
  }

  /**
   * Frees a slot's picture.
   *
   * @param name The slot.
   */
  public release(name: string): void {
    const slot = this.slots.get(name);
    if (slot !== undefined) {
      this.close(name, slot);
    }
  }

  /**
   * Composes the chunks owed, for the first slot that owes any only, the player's chunk and its nearest first: one
   * chunk at least, then more while the budget lasts, never more than `PICTURE_MOST_A_STEP`. Every
   * `PICTURE_RETRY_FRAMES` steps the chunks drawn without a page they lacked are queued again. A chunk that throws is
   * reported and not tried again until then, and a slot whose chunks keep throwing is given up on after
   * `PICTURE_MAX_RETRIES` rounds. Nothing is composed while the page is not active.
   *
   * @param x The player's position across, in tiles.
   * @param y The player's position down, in tiles.
   * @returns True when a chunk was composed.
   */
  public step(x: number, y: number): boolean {
    if (!this.active()) {
      return false;
    }
    this.frames += 1;
    if (this.frames % PICTURE_RETRY_FRAMES === 0) {
      this.retry();
    }
    for (let index = 0; index < this.list.length; index += 1) {
      const slot = this.list[index] as Slot;
      if (slot.done) {
        continue;
      }
      if (this.cache.pending(slot.key, slot.scale) === 0) {
        slot.done = true;
        continue;
      }
      this.near.cx = Math.floor(x / this.map.chunkSize);
      this.near.cy = Math.floor(y / this.map.chunkSize);
      const started = this.now();
      let composed = 0;
      try {
        do {
          composed += this.cache.step(slot.key, slot.scale, this.near);
        } while (composed < PICTURE_MOST_A_STEP && this.cache.pending(slot.key, slot.scale) > 0 && this.now() - started < this.budgetMs);
      } catch (error) {
        slot.changes += 1;
        slot.threw = true;
        this.fail(error);
        return true;
      }
      if (composed > 0) {
        slot.changes += 1;
        this.composedTotal += composed;
        this.mostInAStep = Math.max(this.mostInAStep, composed);
      }
      return composed > 0;
    }
    return false;
  }

  /**
   * Queues again the chunks each slot drew without a page they lacked or that threw. A slot that threw in the round
   * just ended counts it as a bad round, and past `PICTURE_MAX_RETRIES` bad rounds its chunks are left as they are.
   */
  private retry(): void {
    for (const slot of this.list) {
      if (slot.threw) {
        slot.threw = false;
        slot.badRounds += 1;
      }
      if (slot.badRounds > PICTURE_MAX_RETRIES) {
        continue;
      }
      if (this.cache.retryIncomplete(slot.key, slot.scale) > 0) {
        slot.done = false;
      }
    }
  }

  /**
   * Frees every picture, as when the scene shuts.
   */
  public clear(): void {
    this.cache.clear();
    this.slots.clear();
    this.list.length = 0;
  }
}
